import { Button, Empty, Flex, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { ListEmptyState } from "../../../components/Table/ListEmptyState.tsx"

const { Text } = Typography

interface LeadsEmptyProps {
  hasSearch?: boolean
  /** The smart filter in force, when one is what emptied the table. */
  activeFilterLabel?: string | null
  /** Number of filters in force, search aside. */
  activeFilterCount?: number
  onClearFilters?: () => void
  /** The first page failed to load; an empty table then says nothing about the leads. */
  loadFailed?: boolean
  onRetry?: () => void
}

/**
 * An empty table has to say which of the four reasons it is empty: the load
 * failed, nothing exists yet, the search matched nothing, or a filter is
 * hiding everything. A failed load wins: it says nothing about the other three.
 * The third is the one that reads as a broken page — the controls look neutral
 * while the table is blank — so it names the filter and offers the way out.
 */
export function LeadsEmpty({
  hasSearch,
  activeFilterLabel,
  activeFilterCount = 0,
  onClearFilters,
  loadFailed,
  onRetry
}: LeadsEmptyProps) {
  const { t } = useTranslation()

  if (loadFailed) {
    return <ListEmptyState object="leads" variant="error" onRetry={onRetry} />
  }

  if (hasSearch) {
    return (
      <Empty
        description={
          <Flex vertical className="text-center py-3">
            <Text type={"secondary"} strong>
              {t("leadsTable.empty.noMatch")}
            </Text>
            <Text type={"secondary"} className=" mt-1">
              {t("leadsTable.empty.tryDifferent")}
            </Text>
          </Flex>
        }
      />
    )
  }

  if (activeFilterCount > 0) {
    return (
      <Empty
        description={
          <Flex vertical align={"center"} className="text-center py-3">
            <Text type={"secondary"} strong>
              {activeFilterLabel
                ? t("leadsTable.empty.filtered", { filter: activeFilterLabel })
                : t("leadsTable.empty.filteredGeneric")}
            </Text>
            <Text type={"secondary"} className={"mt-1"}>
              {t("leadsTable.empty.filteredHint")}
            </Text>
            {onClearFilters && (
              <Button
                type={"link"}
                className={"mt-1"}
                onClick={onClearFilters}
                data-testid="leads-empty-clear"
              >
                {t("leadsTable.empty.showAll")}
              </Button>
            )}
          </Flex>
        }
      />
    )
  }

  return <Empty description={t("leadsTable.empty.none")} />
}
